/**
 * Intérprete determinista (sin IA, sin red): reconoce un conjunto acotado de órdenes
 * en español, y las más comunes en inglés, y devuelve una propuesta estructurada o
 * una pregunta de aclaración. Nunca modifica datos.
 */
import { addDays, isValidLocalDate, isoWeekday, logicalDateFor, type LocalDate } from '@habitos/engine';
import type { CommandInterpreter, Interpretation, InterpreterContext, Proposal } from './interpreter';

// --- Texto -----------------------------------------------------------------

/**
 * Minúsculas y sin acentos, unidad UTF-16 a unidad: la copia tiene exactamente la misma
 * longitud que el original (también con emojis), así que los índices coinciden.
 */
function fold(s: string): string {
  return s
    .split('')
    .map((c) => c.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()[0] ?? c)
    .join('');
}

/** Texto con copia normalizada alineada: se busca en la normalizada y se recorta en ambas. */
class Text {
  constructor(
    public raw: string,
    public norm: string = fold(raw),
  ) {}
  /** Quita la primera coincidencia y la devuelve. */
  take(re: RegExp): RegExpExecArray | null {
    const m = re.exec(this.norm);
    if (!m) return null;
    const a = m.index;
    const b = a + m[0].length;
    this.raw = (this.raw.slice(0, a) + ' ' + this.raw.slice(b)).replace(/\s+/g, ' ');
    this.norm = (this.norm.slice(0, a) + ' ' + this.norm.slice(b)).replace(/\s+/g, ' ');
    return m;
  }
  clean(): string {
    return this.raw.replace(/^[\s,.:;-]+|[\s,.:;!?-]+$/g, '').trim();
  }
}

const capitalize = (s: string) => (s ? s[0]!.toLocaleUpperCase() + s.slice(1) : s);

const WEEKDAYS: Record<string, number> = {
  lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6, sabados: 6, domingo: 7, domingos: 7,
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7,
};
const WEEKDAY_RE = '(lunes|martes|miercoles|jueves|viernes|sabados?|domingos?|monday|tuesday|wednesday|thursday|friday|saturday|sunday)';
const MONTHS: Record<string, number> = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6, julio: 7, agosto: 8, septiembre: 9, setiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};
const NUMBER_WORDS: Record<string, number> = { una: 1, un: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, once: 1, twice: 2 };

// --- Fechas y horas --------------------------------------------------------

type TimeResult = { time: string } | { invalid: true } | null;

export function takeTime(t: Text): TimeResult {
  if (t.take(/\b(al |a )?mediodia\b|\bnoon\b/)) return { time: '12:00' };
  let m = t.take(/\b(?:a las?|sobre las?|at)\s+(\d{1,2})(?::(\d{2}))?\s*(?:h|hs|horas)?\s*(?:de la (manana|tarde|noche)|(am|pm))?/);
  if (!m) m = t.take(/\b(\d{1,2}):(\d{2})\s*(?:h|hs)?\s*(?:de la (manana|tarde|noche)|(am|pm))?/);
  if (!m) m = t.take(/\b(\d{1,2})\s*(?:h|hs)\b()()()/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const part = m[3] ?? m[4];
  if ((part === 'tarde' || part === 'noche' || part === 'pm') && h < 12) h += 12;
  if ((part === 'am' || part === 'manana') && h === 12) h = 0;
  if (h > 23 || min > 59) return { invalid: true };
  return { time: `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}` };
}

type DateResult = { date: LocalDate } | { invalid: true } | null;

export function takeDate(t: Text, today: LocalDate, allowPast = false): DateResult {
  if (t.take(/\bpasado manana\b|\bday after tomorrow\b/)) return { date: addDays(today, 2) };
  if (t.take(/\bmanana\b|\btomorrow\b/)) return { date: addDays(today, 1) };
  if (t.take(/\bhoy\b|\btoday\b|\btonight\b|\besta (tarde|noche)\b/)) return { date: today };
  if (allowPast && t.take(/\bayer\b|\byesterday\b/)) return { date: addDays(today, -1) };
  let m = t.take(new RegExp(`\\b(?:el |este |el proximo |proximo |next |on )?${WEEKDAY_RE}\\b`));
  if (m) {
    const target = WEEKDAYS[m[1]!]!;
    const diff = (target - isoWeekday(today) + 7) % 7 || 7;
    return { date: addDays(today, diff) };
  }
  const y = Number(today.slice(0, 4));
  const resolve = (yy: number | null, mm: number, dd: number): DateResult => {
    let year = yy ?? y;
    let d = `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    if (!isValidLocalDate(d)) return { invalid: true };
    if (yy === null && d < today) {
      year += 1;
      d = `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
      if (!isValidLocalDate(d)) return { invalid: true };
    }
    return { date: d as LocalDate };
  };
  m = t.take(/\b(?:el )?(\d{1,2}) de (enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)(?: de (\d{4}))?\b/);
  if (m) return resolve(m[3] ? Number(m[3]) : null, MONTHS[m[2]!]!, Number(m[1]));
  m = t.take(/\b(?:el )?(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
  if (m) return resolve(m[3] ? Number(m[3].length === 2 ? `20${m[3]}` : m[3]) : null, Number(m[2]), Number(m[1]));
  return null;
}

// --- Hábitos: frecuencia y meta -------------------------------------------

type Schedule = Extract<Proposal, { kind: 'create_habit' }>['schedule'];

function takeSchedule(t: Text): Schedule | null {
  if (t.take(/\b(todos los dias|cada dia|a diario|diariamente|diario|every day|daily)\b/)) return { type: 'daily' };
  if (t.take(/\b(de lunes a viernes|entre semana|(los )?dias laborables|laborables|weekdays|on weekdays)\b/)) return { type: 'weekdays', days: [1, 2, 3, 4, 5] };
  if (t.take(/\b(los )?fines? de semana\b|\bweekends?\b/)) return { type: 'weekdays', days: [6, 7] };
  let m = t.take(/\b(\d|una|dos|tres|cuatro|cinco|seis|siete|once|twice)\s*(?:veces|vez|times)?\s*(?:a la|por|por la|cada|a|per|a|each)\s*(?:semana|week)\b/);
  if (m) {
    const n = /^\d$/.test(m[1]!) ? Number(m[1]) : NUMBER_WORDS[m[1]!]!;
    return n >= 7 ? { type: 'daily' } : { type: 'weekly_quota', mode: 'times', times: n };
  }
  m = t.take(new RegExp(`\\bde ${WEEKDAY_RE} a ${WEEKDAY_RE}\\b`));
  if (m) {
    const a = WEEKDAYS[m[1]!]!;
    const b = WEEKDAYS[m[2]!]!;
    const days: number[] = [];
    for (let d = a; ; d = (d % 7) + 1) {
      days.push(d);
      if (d === b || days.length > 7) break;
    }
    return { type: 'weekdays', days: [...new Set(days)].sort() };
  }
  const days = new Set<number>();
  let w: RegExpExecArray | null;
  const re = new RegExp(`\\b(?:los |cada |on )?${WEEKDAY_RE}\\b`);
  while ((w = t.take(re))) days.add(WEEKDAYS[w[1]!]!);
  if (days.size) return { type: 'weekdays', days: [...days].sort() };
  return null;
}

const UNIT_WORDS: [RegExp, string][] = [
  [/^(minutos?|mins?|minutes?)$/, 'min'],
  [/^(horas?|h|hours?)$/, 'h'],
  [/^(km|kilometros?)$/, 'km'],
  [/^(paginas?|pags?|pages?)$/, 'pages'],
  [/^(pasos|steps)$/, 'steps'],
  [/^(ml|mililitros?)$/, 'ml'],
  [/^(l|litros?|liters?)$/, 'l'],
  [/^(kcal|calorias)$/, 'kcal'],
  [/^(g|gramos?|grams?)$/, 'g'],
  [/^(veces|vasos?|times|glasses?)$/, 'count'],
];

function takeAmount(t: Text): { value: number; unit: string } | null {
  const m = t.take(/\b(?:durante |por |for )?(\d+(?:[.,]\d+)?)\s*(minutos?|mins?|minutes?|horas?|hours?|h|km|kilometros?|paginas?|pags?|pages?|pasos|steps|ml|mililitros?|litros?|liters?|l|kcal|calorias|gramos?|grams?|g|veces|vasos?|times|glasses?)\b(?:\s+(?:de|of)\b)?/);
  if (!m) return null;
  const unit = UNIT_WORDS.find(([re]) => re.test(m[2]!))?.[1] ?? 'count';
  return { value: Number(m[1]!.replace(',', '.')), unit };
}

/** Convierte una cantidad a la unidad del hábito cuando es posible (min <-> h). */
function convert(value: number, from: string, to: string | null): number | null {
  if (!to || to === from) return value;
  if (from === 'min' && to === 'h') return Math.round((value / 60) * 100) / 100;
  if (from === 'h' && to === 'min') return Math.round(value * 60);
  if (from === 'count') return value;
  return null;
}

// --- Búsqueda de hábitos ---------------------------------------------------

const STOP = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'que', 'y', 'a', 'en', 'un', 'una', 'mi', 'mis', 'por', 'para', 'con', 'durante', 'hice', 'he', 'hecho', 'the', 'my', 'of', 'to', 'for']);
const words = (s: string) => fold(s).split(/[^a-z0-9]+/).filter((w) => w.length >= 3 && !STOP.has(w));

/** Hábitos cuyo nombre comparte palabras con el texto, del mejor al peor. */
export function matchHabits(text: string, habits: InterpreterContext['habits']): InterpreterContext['habits'] {
  const ws = new Set(words(text));
  const stem = (w: string) => w.slice(0, Math.max(4, w.length - 2));
  const scored = habits
    .map((h) => {
      const hw = words(h.name);
      const score = hw.filter((w) => ws.has(w) || [...ws].some((x) => stem(x) === stem(w))).length;
      return { h, score: hw.length ? score / hw.length : 0 };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);
  if (!scored.length) return [];
  const best = scored[0]!.score;
  return scored.filter((s) => s.score === best).map((s) => s.h);
}

// --- Intenciones -------------------------------------------------------------

const proposal = (p: Proposal): Interpretation => ({ type: 'proposal', proposal: p });
const clarify = (question: string, options: { label: string; proposal: Proposal }[] = [], params: Record<string, string | number> = {}): Interpretation => ({
  type: 'clarify',
  question,
  params,
  options,
});

function splitItems(s: string): string[] {
  return s
    .split(/\s*(?:,|;|\by\b|\be\b|\band\b)\s*/i)
    .map((x) => x.replace(/^(?:un|una|unos|unas|el|la|los|las|some|a|an)\s+/i, '').trim())
    .filter(Boolean)
    .map(capitalize);
}

function reminderOrTask(body: Text, ctx: InterpreterContext, reminder: boolean): Interpretation {
  const time = takeTime(body);
  if (time && 'invalid' in time) return clarify('assistant.q.invalidTime');
  const date = takeDate(body, ctx.today as LocalDate);
  if (date && 'invalid' in date) return clarify('assistant.q.invalidDate');
  body.take(/^\s*(?:que|de|to)\s+/);
  const name = capitalize(body.clean()).slice(0, 80);
  if (!name) return clarify('assistant.q.whatTask');
  let dueDate = date?.date ?? null;
  const dueTime = time?.time ?? null;
  if (dueTime && !dueDate) {
    // Solo hora: hoy si aún no ha pasado; si ya pasó, se pregunta.
    dueDate = ctx.today as LocalDate;
  }
  if (dueDate && dueDate < ctx.today) return clarify('assistant.q.pastDate');
  if (dueDate && dueTime && dueDate === ctx.today && isPast(dueTime, ctx)) {
    return clarify('assistant.q.timePassed', [
      { label: '', proposal: { kind: 'create_task', name, dueDate: addDays(ctx.today as LocalDate, 1), dueTime, reminder } },
    ], { time: dueTime });
  }
  if (reminder && !dueTime) {
    const d = dueDate ?? (ctx.today as LocalDate);
    return clarify('assistant.q.whatTime', [
      { label: '', proposal: { kind: 'create_task', name, dueDate: d, dueTime: '09:00', reminder: true } },
      { label: '', proposal: { kind: 'create_task', name, dueDate: d, dueTime: '18:00', reminder: true } },
      { label: '', proposal: { kind: 'create_task', name, dueDate: dueDate, dueTime: null, reminder: false } },
    ]);
  }
  return proposal({ kind: 'create_task', name, dueDate, dueTime, reminder: reminder && Boolean(dueTime) });
}

function isPast(time: string, ctx: InterpreterContext): boolean {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: ctx.tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(ctx.now);
  return time <= parts;
}

function createHabit(body: Text): Interpretation {
  const quit = Boolean(body.take(/^\s*(?:dejar de|reducir|menos|no|quit|stop)\s+/));
  const schedule = takeSchedule(body) ?? { type: 'daily' as const };
  let max: number | null = null;
  if (quit) {
    const m = body.take(/\b(?:maximo|como maximo|max(?:imo)?|at most)\s+(\d+)\b/);
    if (m) max = Number(m[1]);
  }
  const amount = takeAmount(body);
  body.take(/^\s*(?:para|de|que|to|a)\s+/);
  const name = capitalize(body.clean()).slice(0, 80);
  if (!name) return clarify('assistant.q.whatHabit');
  if (quit) {
    return proposal({ kind: 'create_habit', name, habitKind: 'quit', schedule, goalType: 'numeric', target: max ?? amount?.value ?? 0, unit: amount?.unit ?? 'count' });
  }
  return proposal({
    kind: 'create_habit',
    name,
    habitKind: 'build',
    schedule,
    goalType: amount ? 'numeric' : 'binary',
    target: amount ? amount.value : 1,
    unit: amount ? amount.unit : null,
  });
}

function logActivity(body: Text, ctx: InterpreterContext): Interpretation {
  const date = takeDate(body, ctx.today as LocalDate, true);
  if (date && 'invalid' in date) return clarify('assistant.q.invalidDate');
  const when = date?.date ?? (ctx.today as LocalDate);
  if (when > ctx.today) return clarify('assistant.q.futureLog');
  const amount = takeAmount(body);
  body.take(/^\s*(?:que\s+)?(?:hice|he hecho|hecho|i did|did)\s+/);
  const rest = body.clean();
  const candidates = matchHabits(rest, ctx.habits.filter((h) => h.kind !== 'task' && !h.archived));
  const toProposal = (h: InterpreterContext['habits'][number]): Proposal | null => {
    if (h.goalType === 'binary') return { kind: 'log_activity', habitId: h.id, habitName: h.name, date: when, amount: null, unit: null };
    const value = amount ? convert(amount.value, amount.unit, h.unit) : h.defaultIncrement;
    if (value === null || value <= 0) return null;
    return { kind: 'log_activity', habitId: h.id, habitName: h.name, date: when, amount: value, unit: h.unit };
  };
  if (candidates.length === 1) {
    const p = toProposal(candidates[0]!);
    return p ? proposal(p) : clarify('assistant.q.unitMismatch', [], { name: candidates[0]!.name });
  }
  if (candidates.length > 1) {
    const options = candidates.map((h) => toProposal(h)).filter((p): p is Proposal => p !== null).map((p) => ({ label: '', proposal: p }));
    return clarify('assistant.q.whichHabit', options);
  }
  const name = capitalize(rest.replace(/^(el|la|los|las)\s+/i, '')).slice(0, 80);
  if (!name) return clarify('assistant.q.whichHabit');
  return clarify('assistant.q.noHabit', [
    {
      label: '',
      proposal: amount
        ? { kind: 'create_habit', name, habitKind: 'build', schedule: { type: 'daily' }, goalType: 'numeric', target: amount.value, unit: amount.unit }
        : { kind: 'create_habit', name, habitKind: 'build', schedule: { type: 'daily' }, goalType: 'binary', target: 1, unit: null },
    },
  ], { name });
}

function addToList(itemsText: string, listName: string, ctx: InterpreterContext): Interpretation {
  const items = splitItems(itemsText);
  if (!items.length) return clarify('assistant.q.whatItems');
  const raw = listName.trim().replace(/[.!?]+$/, '');
  const key = fold(raw).replace(/^(la|mi|the|my)\s+/, '');
  const bare = key.replace(/^lista (de |del |para )?/, '').replace(/ list$/, '');
  const existing = ctx.lists.find((l) => {
    const lt = fold(l.title).replace(/^lista (de |del |para )?/, '').replace(/ list$/, '');
    return lt === bare || fold(l.title) === key;
  });
  const title = existing?.title ?? capitalize(/^lista\b/.test(key) || / list$/.test(key) ? raw.replace(/^(la|mi|the|my)\s+/i, '') : `Lista de ${raw}`);
  return proposal({ kind: 'add_to_list', listName: title.slice(0, 80), listId: existing?.id ?? null, items });
}

function destructive(kind: 'archive_habit' | 'delete_habit', target: string, ctx: InterpreterContext): Interpretation {
  const found = matchHabits(target, ctx.habits);
  if (found.length === 1) return proposal({ kind, habitId: found[0]!.id, habitName: found[0]!.name });
  if (found.length > 1) return clarify('assistant.q.whichHabit', found.map((h) => ({ label: '', proposal: { kind, habitId: h.id, habitName: h.name } })));
  return clarify('assistant.q.noHabitToChange', [], { name: target });
}

export function interpretDeterministic(input: string, ctx: InterpreterContext): Interpretation {
  const text = input.trim().replace(/\s+/g, ' ').slice(0, 500);
  if (!text) return { type: 'unsupported' };
  const t = new Text(text);
  const n = t.norm;
  let m: RegExpExecArray | null;

  if ((m = /^(?:por favor\s+)?(archiva(?:r)?|archive|elimina(?:r)?|borra(?:r)?|delete|remove)\s+(?:el\s+|the\s+)?(?:habito\s+|habit\s+)?(.+)$/.exec(n)) && !/\blista\b|\blist\b/.test(n)) {
    const kind = /^archiv/.test(m[1]!) ? 'archive_habit' : 'delete_habit';
    return destructive(kind, text.slice(n.indexOf(m[2]!)), ctx);
  }
  if (/\b(pendientes|que tengo (para )?hoy|mis tareas|mi agenda|agenda de hoy|my tasks|my agenda|what do i have|todo today|to do today)\b/.test(n)) {
    return proposal({ kind: 'show_today' });
  }
  if ((m = /^(?:anad[ei]r?|agrega(?:r)?|pon(?:er)?|mete(?:r)?|apunta(?:r)?|add)\s+(.+?)\s+(?:a|en|to)\s+(?:la |mi |the |my )?((?:lista\b.*)|(?:.+?\s+list))$/.exec(n))) {
    const itemsRaw = text.slice(n.indexOf(m[1]!), n.indexOf(m[1]!) + m[1]!.length);
    const listRaw = text.slice(n.lastIndexOf(m[2]!), n.lastIndexOf(m[2]!) + m[2]!.length);
    return addToList(itemsRaw, listRaw, ctx);
  }
  // Cerebro: datos, citas y contactos. Van antes que los recordatorios: "recuerda que…" es un
  // dato que guardar, "recuérdame…" es un aviso.
  if ((m = /^(?:acuerdate (?:de )?que|recuerda que|no olvides que|ten en cuenta que|para que sepas,? |dato\s*[:,]?)\s*(.+)$/.exec(n))) {
    return proposal({ kind: 'create_note', noteKind: 'fact', title: capitalize(text.slice(n.indexOf(m[1]!)).trim()).slice(0, 120) });
  }
  if ((m = /^(?:guarda(?:r)? (?:esta |la )?(?:frase|cita)|cita|frase)\s*[:,]?\s*(.+)$/.exec(n))) {
    const raw = text.slice(n.indexOf(m[1]!)).trim();
    // "«Hecho es mejor que perfecto» — Sheryl Sandberg" o "… de Sheryl Sandberg".
    const by = /^["«“]?(.+?)["»”]?\s*(?:—|–|-|,)\s*(?:de\s+)?([^,—–-]{2,60})$/.exec(raw) ?? /^["«“](.+?)["»”]\s+de\s+(.{2,60})$/.exec(raw);
    return proposal({ kind: 'create_note', noteKind: 'quote', title: capitalize((by ? by[1]! : raw).replace(/^["«“]|["»”]$/g, '')).slice(0, 120), body: by ? by[2]!.trim() : null });
  }
  if ((m = /^(?:guarda(?:r)? (?:el )?(?:contacto|telefono|numero) de|contacto\s*[:,]?)\s*(.+)$/.exec(n))) {
    const raw = text.slice(n.indexOf(m[1]!)).trim();
    const [name, ...rest] = raw.split(/\s*[,:]\s*/);
    return proposal({ kind: 'create_note', noteKind: 'contact', title: capitalize(name!).slice(0, 120), body: rest.join(', ').slice(0, 500) || null });
  }
  if (/^(?:recuerdame|recordarme|recordar|recuerda(?:me)?|avisame|remind me(?: to)?)\s+/.test(n)) {
    t.take(/^(?:recuerdame|recordarme|recordar|recuerda(?:me)?|avisame|remind me(?: to)?)\s+/);
    return reminderOrTask(t, ctx, true);
  }
  if (/^(?:crear |crea |anade |anadir |agrega |nueva |new |add )?(?:una |a )?(?:tarea|task)\b/.test(n)) {
    t.take(/^(?:crear |crea |anade |anadir |agrega |nueva |new |add )?(?:una |a )?(?:tarea|task)\s*(?::|para|de|to)?\s*/);
    return reminderOrTask(t, ctx, false);
  }
  if (/\b(habito|habit)\b/.test(n) && /^(?:crear|crea|nuevo|quiero|empezar|create|new|start|add|anade|anadir)/.test(n)) {
    t.take(/^.*?\b(?:habito|habit)\b\s*(?::|para|de|to|of)?\s*/);
    return createHabit(t);
  }
  if (/^(?:registra(?:r)?|anota(?:r)? que|apunta(?:r)? que|marca(?:r)?(?: como hecho)?|he hecho|hice|log|i did)\b/.test(n)) {
    t.take(/^(?:registra(?:r)?|anota(?:r)?|apunta(?:r)?|marca(?:r)?(?: como hecho)?|log)\s*(?:que\s+)?/);
    return logActivity(t, ctx);
  }
  if (/^(?:enfocate|enfocarme|concentrarme|modo concentracion|pomodoro|sesion de enfoque|empezar (?:a )?enfo(?:que|carme)|focus)\b/.test(n)) {
    t.take(/^(?:enfocate|enfocarme|concentrarme|modo concentracion|pomodoro|sesion de enfoque|empezar (?:a )?enfo(?:que|carme)|focus)\s*/);
    const amount = takeAmount(t);
    const minutes = amount ? (amount.unit === 'h' ? Math.round(amount.value * 60) : Math.round(amount.value)) : 25;
    if (minutes < 1 || minutes > 180) return clarify('assistant.q.focusRange');
    t.take(/^\s*(?:en|para|con|on|for)\s+/);
    const rest = t.clean();
    const habit = rest ? matchHabits(rest, ctx.habits.filter((h) => !h.archived && !h.completed))[0] : undefined;
    return proposal({ kind: 'start_focus', minutes, habitId: habit?.id ?? null, label: habit ? null : rest ? capitalize(rest).slice(0, 80) : null });
  }
  if ((m = /^(?:querido diario|diario|journal)\s*[:,]?\s*(.+)$/.exec(n))) {
    return proposal({ kind: 'journal', text: text.slice(n.indexOf(m[1]!)).trim() });
  }
  if ((m = /^idea\s*[:,]?\s*(.+)$/.exec(n))) {
    return proposal({ kind: 'create_note', noteKind: 'idea', title: capitalize(text.slice(n.indexOf(m[1]!)).trim()).slice(0, 120) });
  }
  if ((m = /^(?:nota|anota(?:r)?|apunta(?:r)?|guarda(?:r)?|note)\s*[:,]?\s*(.+)$/.exec(n))) {
    return proposal({ kind: 'create_note', noteKind: 'note', title: capitalize(text.slice(n.indexOf(m[1]!)).trim()).slice(0, 120) });
  }
  return { type: 'unsupported' };
}

export const deterministicInterpreter: CommandInterpreter = {
  id: 'deterministic',
  async interpret(text, ctx) {
    return interpretDeterministic(text, ctx);
  },
};

/** Fecha lógica actual para un instante (útil para pruebas y para el contexto). */
export function todayFor(now: number, tz: string, dayStartMinutes: number): LocalDate {
  return logicalDateFor(now, tz, dayStartMinutes);
}
